import React, { useEffect, useMemo, useRef } from 'react';
import Map, { Layer, Marker, Source, MapRef } from 'react-map-gl/mapbox';
import 'mapbox-gl/dist/mapbox-gl.css';
import type { LatLng } from '../../entities/geo/types';
import { MAPBOX_TOKEN, MissingTokenPlaceholder, Pin } from './mapPrimitives';

/**
 * JourneyMap — a journey shown on a map: From → To with a connecting line.
 *
 * Owns its own Mapbox setup so journey-specific behavior (great-circle arcs,
 * multi-stop ordering, popups with stop info, journey-tinted styles) can
 * grow here without bleeding into other map components.
 *
 * Today: two pin markers (green From / red To) with a dashed straight line.
 */

interface JourneyMapProps {
  from: LatLng;
  to: LatLng;
  height?: number;
}

const FROM_COLOR = '#10b981';
const TO_COLOR = '#ef4444';

const JourneyMap: React.FC<JourneyMapProps> = ({ from, to, height = 280 }) => {
  const mapRef = useRef<MapRef | null>(null);

  const lineGeoJson = useMemo(
    () => ({
      type: 'FeatureCollection' as const,
      features: [
        {
          type: 'Feature' as const,
          properties: {},
          geometry: {
            type: 'LineString' as const,
            coordinates: [
              [from.lng, from.lat],
              [to.lng, to.lat],
            ],
          },
        },
      ],
    }),
    [from.lat, from.lng, to.lat, to.lng]
  );

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (!isFinite(from.lat) || !isFinite(from.lng) || !isFinite(to.lat) || !isFinite(to.lng)) return;

    const minLng = Math.min(from.lng, to.lng);
    const maxLng = Math.max(from.lng, to.lng);
    const minLat = Math.min(from.lat, to.lat);
    const maxLat = Math.max(from.lat, to.lat);

    map.fitBounds(
      [
        [minLng, minLat],
        [maxLng, maxLat],
      ],
      { padding: 60, duration: 600, maxZoom: 9 }
    );
  }, [from.lat, from.lng, to.lat, to.lng]);

  if (!MAPBOX_TOKEN) return <MissingTokenPlaceholder height={height} />;

  const initialCenter: [number, number] = [(from.lng + to.lng) / 2, (from.lat + to.lat) / 2];

  return (
    <div style={{ height, borderRadius: 8, overflow: 'hidden', border: '1px solid #e2e8f0' }}>
      <Map
        ref={mapRef}
        mapboxAccessToken={MAPBOX_TOKEN}
        initialViewState={{ longitude: initialCenter[0], latitude: initialCenter[1], zoom: 3 }}
        mapStyle="mapbox://styles/mapbox/streets-v12"
        style={{ width: '100%', height: '100%' }}
      >
        <Source id="journey-line" type="geojson" data={lineGeoJson}>
          <Layer
            id="journey-line-layer"
            type="line"
            layout={{ 'line-cap': 'round', 'line-join': 'round' }}
            paint={{
              'line-color': FROM_COLOR,
              'line-width': 3,
              'line-dasharray': [2, 1.5],
            }}
          />
        </Source>

        <Marker longitude={from.lng} latitude={from.lat} anchor="bottom">
          <Pin color={FROM_COLOR} label="From" />
        </Marker>
        <Marker longitude={to.lng} latitude={to.lat} anchor="bottom">
          <Pin color={TO_COLOR} label="To" />
        </Marker>
      </Map>
    </div>
  );
};

export default JourneyMap;
